import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import VerdictBadge from "@/components/VerdictBadge";
import { AlertTriangle, FileText, Link2, Gavel, ChevronDown } from "lucide-react";
import { deskFor } from "@/lib/deskAccents";

export default function ContentDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(undefined);

  useEffect(() => {
    setItem(undefined);
    api
      .get(`/public/content/${slug}`)
      .then((r) => setItem(r.data))
      .catch(() => setItem(null));
  }, [slug]);

  if (item === undefined)
    return <div className="max-w-3xl mx-auto px-4 py-24 text-ink/55">Loading…</div>;
  if (item === null)
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-editorial text-3xl font-bold text-ink">Story not found</h1>
        <Link to="/" className="mt-4 inline-block text-heritage underline">Back to home</Link>
      </div>
    );

  const isFact = item.type === "fact_check";
  const accent = deskFor(item.category);

  const VERDICT_ORDER = ["true", "mostly_true", "mixed", "misleading", "unproven", "false"];
  const claims = item.claims || [];
  const verdictTotals = VERDICT_ORDER
    .map((v) => ({ verdict: v, count: claims.filter((c) => c.verdict === v).length }))
    .filter((t) => t.count > 0);

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
      <title>{`${item.title} | Be-Apuo Media Intelligence`}</title>
      <meta name="description" content={item.summary || item.subtitle} />
      <meta property="og:title" content={item.title} />
      <meta property="og:description" content={item.summary || item.subtitle} />
      <meta property="og:type" content="article" />
      <meta name="twitter:card" content="summary_large_image" />

      <div className="flex items-center gap-3">
        <span className={`label-caps ${accent.eyebrow}`} data-testid="content-label">{item.label}</span>
        {item.category && <span className="label-caps text-ink/55">/ {item.category}</span>}
      </div>

      <h1 data-testid="content-title" className="mt-4 font-editorial font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-ink">
        {item.title}
      </h1>
      {item.subtitle && <p className="mt-4 text-xl text-ink/75 leading-relaxed">{item.subtitle}</p>}

      <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink/55 border-y border-ink/10 py-4">
        <span>By <strong className="text-ink">{item.author}</strong></span>
        <span data-testid="published-date">Published {new Date(item.first_published_at || item.published_at).toLocaleDateString()}</span>
        {item.last_updated_at && item.last_updated_at !== (item.first_published_at || item.published_at) && (
          <span data-testid="updated-date">· Updated {new Date(item.last_updated_at).toLocaleDateString()}</span>
        )}
        {item.last_corrected_at && (
          <span data-testid="corrected-date" className="text-clay font-medium">· Corrected {new Date(item.last_corrected_at).toLocaleDateString()}</span>
        )}
        {item.revision_number > 1 && <span>· Rev {item.revision_number}</span>}
        {item.approving_editor && <span>· Edited by {item.approving_editor}</span>}
      </div>

      {isFact && (
        <section data-testid="factcheck-box" className="mt-8 border border-ink/15 bg-mist">
          <div className="border-b border-ink/10 px-6 py-4">
            <div className="label-caps text-ink/55">Claim</div>
            <p className="mt-1 font-editorial text-lg italic text-ink">“{item.claim_statement}”</p>
          </div>
          <div className="px-6 py-5 flex flex-wrap items-center gap-4">
            <div className="label-caps text-ink/55">Verdict</div>
            <VerdictBadge verdict={item.verdict} size="lg" />
            <p className="text-ink/75 flex-1 min-w-[200px]">{item.verdict_summary}</p>
          </div>
        </section>
      )}

      {item.corrections?.length > 0 && (
        <div data-testid="correction-notice" className="mt-8 border-l-4 border-clay bg-white px-5 py-4">
          <div className="flex items-center gap-2 font-semibold text-clay">
            <AlertTriangle className="h-4 w-4" /> Corrections
          </div>
          <ul className="mt-2 space-y-1 text-sm text-ink/80">
            {item.corrections.map((c, i) => (
              <li key={i}><time className="font-medium">{new Date(c.corrected_at).toLocaleDateString()}</time>: {c.explanation}</li>
            ))}
          </ul>
        </div>
      )}

      <div
        className="prose-editorial mt-10 max-w-none text-ink"
        dangerouslySetInnerHTML={{ __html: item.body }}
      />

      {claims.length > 0 && (
        <section data-testid="claims-verdicts" className="mt-12 border-t-2 border-ink pt-6">
          <div className="flex items-center gap-2">
            <Gavel className="h-5 w-5 text-gold" />
            <h2 className="font-editorial font-bold text-xl text-ink">Claims &amp; Verdicts</h2>
          </div>
          <p data-testid="claims-count" className="mt-2 text-sm text-ink/70">
            <strong className="text-ink">{claims.length}</strong> claims reviewed
          </p>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="verdict-tallies">
            {verdictTotals.map((t) => (
              <span
                key={t.verdict}
                data-testid={`verdict-total-${t.verdict}`}
                className="inline-flex items-center gap-1.5 text-xs border border-ink/20 px-2 py-1 text-ink/75"
              >
                <VerdictBadge verdict={t.verdict} /> {t.count}
              </span>
            ))}
          </div>
          <div className="mt-6 space-y-3">
            {claims.map((c, i) => (
              <details key={i} data-testid={`claim-${i}`} className="group border border-ink/15 bg-white">
                <summary
                  data-testid={`claim-summary-${i}`}
                  className="flex flex-wrap items-center gap-3 cursor-pointer px-4 py-3 list-none [&::-webkit-details-marker]:hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-heritage"
                >
                  <VerdictBadge verdict={c.verdict} />
                  <span className="flex-1 min-w-[200px] font-editorial text-ink font-medium">{c.statement}</span>
                  {c.made_by && <span className="text-sm text-ink/55">— {c.made_by}</span>}
                  <ChevronDown className="h-4 w-4 text-ink/40 transition-transform group-open:rotate-180" aria-hidden="true" />
                </summary>
                <div className="border-t border-ink/10 px-4 py-4 space-y-4">
                  {c.verdict_summary && <p className="text-ink/80">{c.verdict_summary}</p>}
                  {c.evidence?.length > 0 && (
                    <div>
                      <div className="label-caps text-ink/55">Public evidence</div>
                      <ul className="mt-2 space-y-2">
                        {c.evidence.map((ev, j) => (
                          <li key={j} className="pl-3 border-l-2 border-gold/60 text-sm text-ink/80">{ev}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {c.sources?.length > 0 && (
                    <div>
                      <div className="label-caps text-ink/55">Cited sources</div>
                      <ol className="mt-2 space-y-1 list-decimal list-inside text-sm">
                        {c.sources.map((s, j) => (
                          <li key={j} className="text-ink/80">
                            {s.title}
                            {s.publisher && <span className="text-ink/55"> — {s.publisher}</span>}
                            {s.url && (
                              <a href={s.url} target="_blank" rel="noreferrer" className="ml-2 text-heritage underline underline-offset-2">link</a>
                            )}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              </details>
            ))}
          </div>
        </section>
      )}

      {isFact && item.evidence?.length > 0 && (
        <section className="mt-12 border-t-2 border-ink pt-6">
          <h2 className="font-editorial font-bold text-xl flex items-center gap-2 text-ink"><FileText className="h-5 w-5 text-gold" /> Public Evidence</h2>
          <ul className="mt-4 space-y-3">
            {item.evidence.map((e, i) => (
              <li key={i} className="pl-4 border-l-2 border-gold/60 text-ink/80">{e}</li>
            ))}
          </ul>
        </section>
      )}

      {item.sources?.length > 0 && (
        <section className="mt-12 border-t-2 border-ink pt-6">
          <h2 className="font-editorial font-bold text-xl flex items-center gap-2 text-ink"><Link2 className="h-5 w-5 text-gold" /> Sources</h2>
          <ol className="mt-4 space-y-2 list-decimal list-inside text-sm">
            {item.sources.map((s, i) => (
              <li key={i} className="text-ink/80">
                {s.title}
                {s.publisher && <span className="text-ink/55"> — {s.publisher}</span>}
                {s.url && (
                  <a href={s.url} target="_blank" rel="noreferrer" className="ml-2 text-heritage underline underline-offset-2">link</a>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      {item.tags?.length > 0 && (
        <div className="mt-10 flex flex-wrap gap-2">
          {item.tags.map((t) => (
            <span key={t} className="text-xs border border-ink/20 px-2 py-1 text-ink/70">#{t}</span>
          ))}
        </div>
      )}
    </article>
  );
}
